import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import {
  FaWallet,
  FaHistory,
  FaSearch,
  FaFilter,
  FaDownload,
  FaSync,
  FaEye,
  FaRupeeSign,
  FaCreditCard,
  FaExchangeAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCalendarAlt,
  FaSort,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getWalletTransactions } from "../../features/admin/adminSlice";
import Pagination from "../../components/shared/Pagination";

const WalletTransactions = () => {
  const dispatch = useDispatch();
  const { walletTransactions, isLoading } = useSelector((state) => state.admin);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const isSummaryRoute = location.pathname.includes("/admin/wallet/summary");

  const [filters, setFilters] = useState({
    page: 1,
    limit: isSummaryRoute ? 25 : 10,
    type: isSummaryRoute ? "admin_commission_deducted" : "all",
    status: "all",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [allRecordsForStats, setAllRecordsForStats] = useState(null);

  // Debounce filters to avoid excessive API calls when user is typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      dispatch(getWalletTransactions(filters));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [dispatch, filters]);

  // Clamp current page to available range
  useEffect(() => {
    const pages = walletTransactions?.pagination?.pages || 0;
    if (pages && filters.page > pages) {
      setFilters((prev) => ({ ...prev, page: pages }));
    } else if (filters.page < 1) {
      setFilters((prev) => ({ ...prev, page: 1 }));
    }
  }, [walletTransactions, filters.page]);

  // Fetch all records (ignoring pagination) for accurate stats across all pages
  useEffect(() => {
    const fetchAllForStats = async () => {
      try {
        const token = auth?.user?.token;
        if (!token) return;
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "10000");
        if (filters.type && filters.type !== "all") params.set("type", filters.type);
        if (filters.status && filters.status !== "all") params.set("status", filters.status);
        if (filters.search) params.set("search", filters.search);
        if (filters.dateFrom) params.set("startDate", filters.dateFrom);
        if (filters.dateTo) params.set("endDate", filters.dateTo);
        if (filters.sortBy) params.set("sortBy", filters.sortBy);
        if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);

        const config = { headers: { Authorization: `Bearer ${token}` } };

        let response;
        try {
          response = await axios.get(`${BASE_API_URL}/wallet/admin/transactions?${params.toString()}`, config);
        } catch (err) {
          if (err.response && err.response.status === 404) {
            response = await axios.get(`${BASE_API_URL}/admin/wallet/transactions?${params.toString()}`, config);
          } else {
            throw err;
          }
        }

        const list = Array.isArray(response.data?.data)
          ? response.data.data
          : (response.data?.data?.transactions || []);

        // Apply same allowed type filter used in table
        const allowedTypes = new Set(['ride_payment_received', 'admin_commission_deducted', 'vendor_commission_credited']);
        const displayed = list.filter((rec) => {
          const t = (rec.transaction?.type) || rec.type;
          if (!allowedTypes.has(t)) return false;
          const method = (rec.transaction?.paymentMethod || rec.transaction?.paymentMode || rec.paymentMethod || rec.paymentMode || '').toLowerCase();
          if (method === 'cod' && (t === 'ride_payment_received' || t === 'vendor_commission_credited')) {
            return false;
          }
          return true;
        });
        setAllRecordsForStats(displayed);
      } catch (e) {
        console.error("Failed to fetch all transactions for stats:", e);
      }
    };

    fetchAllForStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.token, filters.type, filters.status, filters.search, filters.dateFrom, filters.dateTo, filters.sortBy, filters.sortOrder]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // No need to dispatch here as the filters have already changed
    // and the debounced effect will handle the dispatch
  };

  const handleRefresh = () => {
    dispatch(getWalletTransactions(filters));
    toast.success("Transactions refreshed successfully");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `${num}%` : 'N/A';
  };

  const getSafeBookingId = (bId, bNum) => {
    if (bNum) return String(bNum);
    if (typeof bId === 'object' && bId !== null) {
      return bId.bookingId || bId.bookingNumber || bId.customBookingId || bId._id || '';
    }
    return bId ? String(bId) : '';
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      // Driver wallet transaction types
      case "commission_deduction":
      case "commission_cut":
      case "cod_commission_deduction":
      case "cod_admin_commission_deduction":
      case "cod_vendor_commission_deduction":
      case "admin_commission_deduction":
      case "vendor_commission_deduction":
        return <FaRupeeSign className="text-red-600" />;
      case "withdrawal_approved":
      case "withdrawal_rejected":
        return <FaCreditCard className="text-blue-500" />;
      case "ride_earning":
      case "online_ride_earning":
        return <FaWallet className="text-green-600" />;
      case "refund":
        return <FaExchangeAlt className="text-orange-500" />;
      case "adjustment":
        return <FaSync className="text-indigo-500" />;
      // Vendor commission transaction types (to match vendor format)
      case "ride_payment_received":
        return <FaWallet className="text-green-600" />;
      case "admin_commission_deducted":
        return <FaRupeeSign className="text-red-600" />;
      case "vendor_commission_credited":
        return <FaRupeeSign className="text-red-600" />;
      case "paid_to_driver":
        return <FaArrowUp className="text-blue-600" />;
      default:
        return <FaWallet className="text-gray-500" />;
    }
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      // Driver wallet transaction types
      commission_deduction: "Commission Deduction",
      commission_cut: "Commission Deduction",
      admin_commission_deduction: "Admin Commission Deducted",
      vendor_commission_deduction: "Vendor Commission Deducted",
      cod_commission_deduction: "COD Commission Deduction",
      cod_admin_commission_deduction: "COD Admin Commission Deduction",
      cod_vendor_commission_deduction: "COD Vendor Commission Deduction",
      withdrawal_approved: "Withdrawal Approved",
      withdrawal_rejected: "Withdrawal Rejected",
      ride_earning: "Ride Payment Received",
      online_ride_earning: "Ride Payment Received",
      refund: "Refund",
      adjustment: "Manual Adjustment",
      // Vendor commission transaction types (for consistent display)
      ride_payment_received: "Ride Payment Received",
      admin_commission_deducted: "Admin Commission Credited",
      vendor_commission_credited: "Vendor Commission Deducted",
      paid_to_driver: "PAID To Driver",
    };
    return labels[type] || (typeof type === 'string' ? type.replace(/_/g, " ") : "Transaction");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: "green", icon: FaCheckCircle, label: "Completed" },
      pending: { color: "yellow", icon: FaClock, label: "Pending" },
      failed: { color: "red", icon: FaTimesCircle, label: "Failed" },
      cancelled: { color: "gray", icon: FaTimesCircle, label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.completed;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-${config.color}-100 text-${config.color}-800`}
      >
        <Icon className="mr-1" />
        {config.label}
      </span>
    );
  };

  const openTransactionModal = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleSort = (field) => {
    const newOrder =
      filters.sortBy === field && filters.sortOrder === "desc" ? "asc" : "desc";
    handleFilterChange("sortBy", field);
    handleFilterChange("sortOrder", newOrder);
  };

  const getSortIcon = (field) => {
    if (filters.sortBy !== field) return <FaSort className="text-gray-400" />;
    return filters.sortOrder === "desc" ? (
      <FaArrowDown className="text-blue-500" />
    ) : (
      <FaArrowUp className="text-blue-500" />
    );
  };

  const transactionTypes = [
    { value: "all", label: "All Types" },
    { value: "ride_payment_received", label: "Ride Payment Received" },
    { value: "admin_commission_deducted", label: "Admin Commission Credited" },
    { value: "vendor_commission_credited", label: "Vendor Commission Deducted" },
  ];

  const statusTypes = [
    { value: "all", label: "All Status" },
    { value: "completed", label: "Completed" },
    { value: "pending", label: "Pending" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const getTransactionStats = () => {
    const fromAll = Array.isArray(allRecordsForStats) ? allRecordsForStats : null;
    const baseList = fromAll || (Array.isArray(walletTransactions)
      ? walletTransactions
      : (walletTransactions?.transactions || []));

    const allowedTypes = new Set([
      'ride_payment_received',
      'admin_commission_deducted',
      'vendor_commission_credited',
    ]);

    const list = baseList.filter(rec => {
      const t = (rec.transaction?.type) || rec.type;
      if (!allowedTypes.has(t)) return false;
      const method = (rec.transaction?.paymentMethod || rec.transaction?.paymentMode || rec.paymentMethod || rec.paymentMode || '').toLowerCase();
      if (method === 'cod' && (t === 'ride_payment_received' || t === 'vendor_commission_credited')) return false;
      return true;
    });

    const total = list.length;
    const totalAmount = list.reduce((sum, rec) => sum + (rec.transaction?.amount || rec.amount || 0), 0);
    const commissions = list.filter(rec => {
      const t = (rec.transaction?.type) || rec.type;
      return t === 'admin_commission_deducted' || t === 'vendor_commission_credited';
    });

    return {
      total,
      totalAmount,
      commissionsCount: commissions.length,
      withdrawalsCount: 0,
    };
  };

  const stats = getTransactionStats();

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div className="flex items-center mb-4 sm:mb-0">
          <FaHistory className="text-3xl text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wallet Transactions</h1>
            <p className="text-gray-600 mt-1">
              View and manage all wallet transaction history
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaSync className="mr-2" />
            Refresh
          </button>

          <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FaDownload className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <FaHistory className="text-2xl text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.totalAmount)}
                </p>
              </div>
              <FaRupeeSign className="text-2xl text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Commissions</p>
                <p className="text-2xl font-bold text-purple-600">{stats.commissionsCount}</p>
              </div>
              <FaRupeeSign className="text-2xl text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Withdrawals</p>
                <p className="text-2xl font-bold text-orange-600">{stats.withdrawalsCount}</p>
              </div>
              <FaCreditCard className="text-2xl text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Transaction Type */}
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {transactionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            {/* Status */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusTypes.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>

            {/* Per Page */}
            {!isSummaryRoute && (
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange("limit", parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaFilter className="mr-2" />
              Apply Filters
            </button>
          </div>
        </form>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="table-scroll-container">
            <div style={{ minWidth: '1800px' }}>
              <table className="admin-table" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center">
                      Date & Time
                      {getSortIcon("createdAt")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("amount")}
                  >
                    <div className="flex items-center">
                      Amount
                      {getSortIcon("amount")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const baseList = Array.isArray(walletTransactions)
                    ? walletTransactions
                    : (walletTransactions?.transactions || []);

                  // Show all transaction types on admin transactions page
                  const displayed = baseList.slice();

                  return displayed.map((record) => {
                    const tx = record.transaction || record; // fallback if already flat
                    return (
                      <tr key={tx._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaCalendarAlt className="text-gray-400 mr-2" />
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">
                                {formatDate(tx.createdAt || tx.date)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getTransactionIcon(tx.type)}
                            <div className="ml-2">
                              <div className="text-sm font-medium text-gray-900">
                                {getTransactionTypeLabel(tx.type)}
                              </div>
                              {(tx.type === 'admin_commission_deducted' || tx.type === 'admin_commission_deduction' || tx.type === 'cod_admin_commission_deduction') && (
                                <div className="text-xs text-gray-500">
                                  This is your fee ({formatPercentage(tx?.commissionBreakdown?.adminCommission ?? tx?.commissionRate)})
                                </div>
                              )}
                              {(tx.type === 'vendor_commission_credited' || tx.type === 'vendor_commission_deduction' || tx.type === 'cod_vendor_commission_deduction') && (
                                <div className="text-xs text-gray-500">
                                  Vendor earning ({formatPercentage(tx?.commissionBreakdown?.vendorCommission ?? tx?.commissionRate)})
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {record.driverName || tx.driverId?.name || tx.driverName || "Unknown"}
                            </div>
                            <div className="text-gray-500">
                              {tx.driverId?.email || tx.driverEmail || ""}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const debitTypes = new Set([
                              'commission_deduction',
                              'admin_commission_deduction',
                              'vendor_commission_deduction',
                              'vendor_commission_credited',
                              'paid_to_driver',
                              'cod_commission_deduction',
                              'cod_admin_commission_deduction',
                              'cod_vendor_commission_deduction',
                              'cod_commission_settlement',
                              'withdrawal_approved',
                            ]);
                            const isDebit = debitTypes.has(tx.type);
                            const color = isDebit ? 'text-red-600' : 'text-green-600';
                            const sign = isDebit ? '-' : '+';
                            return (
                              <span className={`text-sm font-bold ${color}`}>
                                {sign}{formatCurrency(Math.abs(tx.amount || 0))}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(tx.status || "completed")}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {tx.description || tx.notes || "No description"}
                          </div>
                          {tx.referenceId && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                              Ref: {tx.referenceId}
                            </div>
                          )}
                          {getSafeBookingId(tx.bookingId, tx.bookingNumber) && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                              Booking: {getSafeBookingId(tx.bookingId, tx.bookingNumber)}
                            </div>
                          )}
                          {tx.paymentMethod && (
                            <div className="text-xs text-gray-500 mt-1">
                              Method: {tx.paymentMethod}
                            </div>
                          )}
                          {tx.paymentProofImage && (
                            <div className="text-xs text-green-600 font-medium mt-1 flex items-center">
                              <span className="mr-1">📷</span>
                              <button
                                onClick={() => window.open(tx.paymentProofImage, '_blank')}
                                className="hover:text-green-700 underline"
                              >
                                Payment Proof
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openTransactionModal({
                              ...tx,
                              driverName: record.driverName || tx.driverName,
                              driverEmail: record.driverEmail || tx.driverEmail,
                              driverPhone: record.driverPhone || tx.driverPhone,
                            })}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

            {(() => {
              const baseList = Array.isArray(walletTransactions) ? walletTransactions : (walletTransactions?.transactions || []);
              const displayed = baseList.slice();
              return displayed.length === 0;
            })() && (
                <div className="text-center py-12">
                  <FaHistory className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search criteria.
                  </p>
                </div>
              )}
          </div>
        )}

        {/* Pagination */}
        {(() => {
          // Always trust totalItems, not pages from backend
          const totalItems =
            walletTransactions?.total ??
            walletTransactions?.pagination?.total ??
            0;

          // Calculate pages properly
          const totalPages =
            totalItems > 0 ? Math.ceil(totalItems / filters.limit) : 0;

          // Hide pagination if no items
          if (totalPages <= 1) return null;

          return (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) =>
                  handleFilterChange("page", Math.max(1, Math.min(page, totalPages)))
                }
                totalItems={totalItems}
                itemsPerPage={filters.limit}
                showWhenSinglePage={true}
              />
            </div>
          );
        })()}

      </div>

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Transaction Details
                </h3>
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Transaction Info</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">ID:</span> {selectedTransaction._id}
                      </div>
                      <div>
                        <span className="font-medium">Type:</span>{" "}
                        {getTransactionTypeLabel(selectedTransaction.type)}
                      </div>
                      <div>
                        <span className="font-medium">Amount:</span>{" "}
                        <span
                          className={`font-bold ${selectedTransaction.amount > 0
                              ? "text-green-600"
                              : "text-red-600"
                            }`}
                        >
                          {formatCurrency(selectedTransaction.amount)}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Status:</span>{" "}
                        {getStatusBadge(selectedTransaction.status || "completed")}
                      </div>
                      <div>
                        <span className="font-medium">Date:</span>{" "}
                        {formatDate(selectedTransaction.createdAt || selectedTransaction.date)}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Driver Info</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Name:</span>{" "}
                        {selectedTransaction.driverName || selectedTransaction.driverId?.name || "Unknown"}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span>{" "}
                        {selectedTransaction.driverEmail || selectedTransaction.driverId?.email || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span>{" "}
                        {selectedTransaction.driverPhone || selectedTransaction.driverId?.phone || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Commission & GST Breakdown */}
                {(selectedTransaction.commissionAmount !== undefined || selectedTransaction.gstAmount !== undefined || selectedTransaction.breakdownDetails) && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Commission & GST Breakdown</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 p-3 rounded-lg">
                      {selectedTransaction.commissionAmount !== undefined && (
                        <div>
                          <span className="text-gray-600">Commission (10%):</span>
                          <p className="font-semibold text-gray-900">{formatCurrency(selectedTransaction.commissionAmount)}</p>
                        </div>
                      )}
                      {selectedTransaction.gstAmount !== undefined && (
                        <div>
                          <span className="text-gray-600">GST (18%):</span>
                          <p className="font-semibold text-gray-900">{formatCurrency(selectedTransaction.gstAmount)}</p>
                        </div>
                      )}
                      {selectedTransaction.breakdownDetails && (
                        <>
                          <div>
                            <span className="text-gray-600">Commission:</span>
                            <p className="font-semibold text-gray-900">{formatCurrency(selectedTransaction.breakdownDetails.commission)}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">GST:</span>
                            <p className="font-semibold text-gray-900">{formatCurrency(selectedTransaction.breakdownDetails.gst)}</p>
                          </div>
                          <div className="col-span-2 border-t border-gray-300 pt-2 mt-2">
                            <span className="text-gray-600">Total:</span>
                            <p className="font-bold text-gray-900">{formatCurrency(selectedTransaction.breakdownDetails.total)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                {(selectedTransaction.description || selectedTransaction.notes) && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {selectedTransaction.description || selectedTransaction.notes}
                    </p>
                  </div>
                )}

                {/* Additional Details */}
                {(selectedTransaction.bookingId || selectedTransaction.referenceId || selectedTransaction.paymentProofImage) && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Additional Details</h4>
                    <div className="space-y-2 text-sm">
                      {getSafeBookingId(selectedTransaction.bookingId, selectedTransaction.bookingNumber) && (
                        <div>
                          <span className="font-medium">Booking ID:</span>{" "}
                          {getSafeBookingId(selectedTransaction.bookingId, selectedTransaction.bookingNumber)}
                        </div>
                      )}
                      {selectedTransaction.referenceId && (
                        <div>
                          <span className="font-medium">Reference ID:</span>{" "}
                          {selectedTransaction.referenceId}
                        </div>
                      )}
                      {selectedTransaction.paymentProofImage && (
                        <div>
                          <span className="font-medium">Payment Proof:</span>{" "}
                          <button
                            onClick={() => window.open(selectedTransaction.paymentProofImage, '_blank')}
                            className="text-blue-600 hover:text-blue-700 underline ml-2"
                          >
                            View Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletTransactions;
